/**
 * What the page may tell the backend about a document read, and what it may put in a console.
 *
 * The read itself happens between this browser and the workstation service on the loopback; the
 * backend is deliberately not on that path and never sees the document. What it does get is a
 * short statement that a read happened — provider, opaque handles, timings — attached to the save
 * it produced. This module builds that statement, and it is the only thing that does, so the set
 * of fields leaving the browser is one list in one file.
 *
 * The scan session this is built from carries far more than the audit may have: a decoded result,
 * image handles, the reader's serial suffix and the service's own error text. None of them are
 * read here. Copying the session and deleting fields would be the fragile way round; this picks
 * the six it is allowed to send and cannot pick up a seventh by accident.
 */

/** The two ways this application can read an identity document. */
export type ScanAuditProvider = 'chip' | 'optical';

/**
 * What a read reported about the card photo, exactly as the workstation service published it.
 *
 * Carried through unchanged rather than re-derived. The service is the only thing that knows what
 * happened to the photo, and a page that recomputed it from the read's overall status would
 * reintroduce the very confusion the status was added to remove.
 */
export interface PhotoOutcomeFacts {
    readonly state: string;
    readonly code: string;
}

/** Everything the page knows about a read that the backend is allowed to be told. */
export interface ScanProvenance {
    readonly provider: ScanAuditProvider;
    /** The workstation's pairing identity. The backend verifies it; this only carries it. */
    readonly installationId: string | null;
    readonly sessionId: string | null;
    readonly deviceId: string | null;
    /** ISO-8601, as the service reported them. */
    readonly startedAt: string | null;
    readonly endedAt: string | null;
    /**
     * What the read said about the photo, or null when it said nothing.
     *
     * Null is sent as an absent field, which the backend stores as "not reported". An optical read
     * has no card photo to report on and always carries null.
     */
    readonly photo: PhotoOutcomeFacts | null;
    /**
     * What the read decided about the printed address block, or null when it said nothing.
     *
     * A code, never a value. The address itself is never sent here even when one was proposed and
     * the operator applied it - what is recorded is which branch of the address pass ran, which is
     * a fact about this application rather than about the card.
     */
    readonly address: string | null;
}

/** The `document_scan` block, exactly as the backend's allowlist accepts it. */
export interface ScanAuditBlock {
    provider: ScanAuditProvider;
    deviceId?: string;
    sessionId?: string;
    startedAt?: string;
    endedAt?: string;
    /** Sent as a pair or not at all; the backend refuses one without the other. */
    photoState?: string;
    photoCode?: string;
    /** An allowlisted address outcome code. Never an address, and never OCR text. */
    addressOutcome?: string;
}

/** What a save carries when it came from a read. */
export interface ScanAuditFields {
    document_scan: ScanAuditBlock;
    installation_id?: string;
}

/**
 * The session fields this module is allowed to read.
 *
 * Structurally typed rather than importing `OpticalScanSession`, so that widening the session with
 * a new decoded field cannot widen what this sends: a type that named the whole session would keep
 * compiling as it grew.
 */
export interface ScannedSessionFacts {
    readonly sessionId: string;
    readonly deviceId: string;
    readonly createdAt: string;
    readonly terminalAt: string | null;
}

/** Adds a key only when there is a value, so an absent field is absent rather than null. */
function withOptional(
    block: ScanAuditBlock,
    key: 'deviceId' | 'sessionId' | 'startedAt' | 'endedAt',
    value: string | null,
): void {
    if (value !== null && value !== '') {
        block[key] = value;
    }
}

/** Builds the block a save or a portrait upload carries. */
export function buildScanAuditFields(provenance: ScanProvenance): ScanAuditFields {
    const block: ScanAuditBlock = { provider: provenance.provider };

    withOptional(block, 'deviceId', provenance.deviceId);
    withOptional(block, 'sessionId', provenance.sessionId);
    withOptional(block, 'startedAt', provenance.startedAt);
    withOptional(block, 'endedAt', provenance.endedAt);

    // Both halves or neither: the backend rejects a lone one rather than guessing at the other,
    // and a read that reported nothing about the photo must send nothing about it.
    if (provenance.photo !== null) {
        block.photoState = provenance.photo.state;
        block.photoCode = provenance.photo.code;
    }

    // Tested for its type rather than against null, so a caller that omits the field entirely -
    // an older page, or a partial fixture - adds no key at all rather than one holding undefined.
    if (typeof provenance.address === 'string' && provenance.address !== '') {
        block.addressOutcome = provenance.address;
    }

    const fields: ScanAuditFields = { document_scan: block };

    if (provenance.installationId !== null && provenance.installationId !== '') {
        fields.installation_id = provenance.installationId;
    }

    return fields;
}

/**
 * The provenance of an optical read: the session's own handles, plus what the address pass decided.
 *
 * @param session The scan session's opaque handles and timings.
 * @param installationId The workstation's pairing identity, or null.
 * @param addressOutcome The address pass's code, or null when the service reported none - which is
 * what a service built before the address pass sends, and what is stored as "not reported".
 */
export function opticalProvenance(
    session: ScannedSessionFacts,
    installationId: string | null,
    addressOutcome: string | null = null,
): ScanProvenance {
    return {
        provider: 'optical',
        installationId,
        sessionId: session.sessionId,
        deviceId: session.deviceId,
        startedAt: session.createdAt,
        endedAt: session.terminalAt,
        // An optical read produces no card photo, so it has nothing to report here. That is
        // "reported nothing", not "unknown": the question does not arise for this provider.
        photo: null,
        // Carried through exactly as the service stated it. The browser does not re-derive this:
        // the address pass runs on the workstation and is the only thing that knows which branch
        // it took, and a page that recomputed it from what it can see would be guessing.
        address: addressOutcome,
    };
}

/**
 * The provenance of a chip read. The chip path has no session and no device handle.
 *
 * @param photo What the service reported about the card photo, or null when it reported nothing -
 * which is what an older workstation service produces, and is stored as "not reported".
 */
export function chipProvenance(
    installationId: string | null,
    photo: PhotoOutcomeFacts | null = null,
): ScanProvenance {
    return {
        provider: 'chip',
        installationId,
        sessionId: null,
        deviceId: null,
        startedAt: null,
        endedAt: null,
        photo,
        // The chip path reads no printed address block, so the question does not arise for it.
        address: null,
    };
}

/** The three endings a workstation may report about a scan of its own. */
export type ReportableScanEvent = 'scan_completed' | 'scan_failed' | 'scan_cancelled';

/** What to file about a scan nobody claimed. */
export interface UnclaimedScanEnding {
    readonly eventType: ReportableScanEvent;
    readonly outcomeCode: string;
}

/** What is known about a scan at the moment the operator leaves it behind. */
export interface UnclaimedScanFacts {
    /** Whether a read actually produced a document. */
    readonly hasResult: boolean;
    /** A transport or service error, as the hook reports it. */
    readonly error: string | null;
    /** Why a scan that ran produced nothing. */
    readonly failure: string | null;
    /** Whether the service ended the session as cancelled. */
    readonly cancelled: boolean;
    /** Whether the scan had reached a terminal state at all. */
    readonly finished: boolean;
}

/**
 * A session created but never read from, as distinct from one that read and failed.
 *
 * The distinction is the point. "No document was ever presented" and "a document was presented and
 * could not be read" are different facts about a reader, and an audit that recorded both as a
 * generic cancellation could not tell them apart afterwards.
 */
export const SCAN_ABANDONED_BEFORE_READ = 'SCAN_ABANDONED_BEFORE_READ';

/**
 * What to record about a scan the operator walked away from.
 *
 * Every session this application creates gets a row, and the row says which of five things
 * happened. Physical acceptance on 2026-09-03 proved why: four real reads of a real identity
 * document were performed and only two were recorded, because the only path that filed a completed
 * read was closing the modal — Retry replaced the session and said nothing about the one it
 * replaced. Two genuine reads left no central trace at all.
 *
 * Order matters and is not arbitrary. A result outranks everything, because a scan that produced a
 * document produced one whatever else is also true of the session; a cancellation outranks an
 * error, because the operator's own instruction is the better description of an ending they asked
 * for; and only when none of those apply does the absence of a terminal state become the answer.
 */
export function classifyUnclaimedScan(facts: UnclaimedScanFacts): UnclaimedScanEnding {
    if (facts.hasResult) {
        // Completed, and then left behind. `OK` describes the read, not the operator's decision:
        // whether anything was kept is `data_applied`, which only the save may assert.
        return { eventType: 'scan_completed', outcomeCode: 'OK' };
    }

    if (facts.cancelled) {
        return { eventType: 'scan_cancelled', outcomeCode: 'SCAN_CANCELLED' };
    }

    if (facts.error !== null) {
        return { eventType: 'scan_failed', outcomeCode: facts.error };
    }

    if (facts.failure !== null) {
        return { eventType: 'scan_failed', outcomeCode: facts.failure };
    }

    if (!facts.finished) {
        // Created, and abandoned while it was still waiting. No read was attempted, so calling this
        // a failed read would put a reader's name against something the reader never did.
        return { eventType: 'scan_cancelled', outcomeCode: SCAN_ABANDONED_BEFORE_READ };
    }

    // Terminal, with no result and nothing that says why. Rare, and still worth a row: a scan that
    // ended holding nothing is exactly what `SCAN_NOT_COMPLETED` means.
    return { eventType: 'scan_failed', outcomeCode: 'SCAN_NOT_COMPLETED' };
}

/** How much of a failure may be written to a console before it stops being a summary. */
const MAX_CONSOLE_DETAIL = 200;

/**
 * A console-safe description of a failure.
 *
 * `console.error('save failed:', error)` looks harmless and is not. A rejected request carries the
 * body it sent, so logging the error object on a client save publishes the customer's name,
 * personal numeric code and identity document number to the browser console, and on a portrait
 * upload it publishes their face as base64. Neither is recoverable once a support screenshot
 * exists.
 *
 * This returns the one thing worth having — what went wrong — and cannot return a payload, because
 * it only ever reads `message` off an `Error`. Anything else is described by its type.
 */
export function describeFailure(error: unknown): string {
    if (error instanceof Error) {
        return error.message.slice(0, MAX_CONSOLE_DETAIL);
    }

    if (typeof error === 'string') {
        return error.slice(0, MAX_CONSOLE_DETAIL);
    }

    return `a non-Error failure of type ${typeof error}`;
}
